import Polyfill from "./Polyfill";
import { icon } from "../../common/js/icon";


/**
 * this class is the basic modal of Event Gallery.
 */
export default class Overlay {

    /**
     * @param parent HTML element to append the modal to.
     */
    constructor(parent = document.body) {
        this.isOpen = false;
        this.myDialogElement = null; // This will hold the dialog element
        this.parent = parent
        this.onClosed = undefined;
        this.onLoad = undefined;
        // aborted when the dialog closes: what a content script listens to on the document ends with it
        this.abortController = null;
    }

    /**
     * The signal of the open dialog: a script which brings its content to life registers its
     * listeners on the document with it, and they go when the dialog closes. Until 7.0.0 every
     * opening of the format dialog left three listeners on the document behind.
     *
     * @returns {AbortSignal|undefined}
     */
    get signal() {
        return this.abortController ? this.abortController.signal : undefined;
    }

    /**
     * opens an overlay. The content can be a url or content.
     * Url start with http or /
     *
     * @param dataUrl
     * @param fetchOptions optional init object for fetch(), e.g. to POST a body
     */
    openOverlay(dataUrl, fetchOptions = undefined) {

        this.isOpen = true;
        this.abortController = new AbortController();

        // Remove existing overlay if any (though usually we create a new instance)
        const existing = document.getElementById('eventgallery-overlay');
        if (existing) {
            existing.remove();
        }

        this.myDialogElement = document.createElement('dialog');
        this.myDialogElement.id = 'eventgallery-overlay';
        // the frame of the design system (components/dialog.less); the id stays as the script hook
        this.myDialogElement.className = 'eg-dialog';

        // a spinner until the content arrives, which a screen reader announces as "Loading …"
        // while the dialog is busy (until 7.0.0 the spinner was a glyph without a word)
        this.myDialogElement.setAttribute('aria-busy', 'true');
        this.myDialogElement.innerHTML = '<div class="eg-dialog__loading" role="status">' + icon('spinner', 'eg-icon--lg eg-icon--spin')
            + '<span class="eg-sr-only">' + Overlay._escape(this._loadingText()) + '</span></div>';

        this.parent.append(this.myDialogElement);

        // Native dialog has a ::backdrop, so we don't need a separate background div unless for specific styling
        // However, we want to close on backdrop click if desired.
        this.myDialogElement.addEventListener('click', (event) => {
            if (event.target === event.currentTarget) {
                this.closeOverlay();
            }
        });

        // The browser closes a modal dialog by itself on Escape (and on the back gesture of
        // Android, which takes the same way): the overlay learns it from the close event, so
        // every way out ends in closeOverlay(). Until 7.0.0 only the close button and the
        // backdrop did, isOpen stayed true after Escape and the lightbox, which asks for it,
        // ignored every key from then on (LB-1).
        this.myDialogElement.addEventListener('close', (event) => {
            if (event.target === this.myDialogElement) {
                this.closeOverlay();
            }
        });

        this.myDialogElement.showModal();

        if (dataUrl.startsWith('http') || dataUrl.startsWith('/')) {
            Overlay._getRemoteData(dataUrl, (data) => {
                this._addContent(data);
            }, fetchOptions, () => {
                this._showLoadError();
            })
        } else {
            // content of the page (a[data-eventgallery-overlay]): a header with the close button and a body
            this._addContent('<div class="eg-dialog__header"><div class="eg-dialog__heading"></div>'
                + '<button type="button" class="eg-btn eg-btn--quiet eg-btn--icon eg-dialog__close eventgallery-close-overlay" aria-label="' + this._closeLabel() + '">'
                + icon('close') + '</button></div><div class="eg-dialog__body">' + dataUrl + '</div>');
        }
    }

    _closeLabel() {
        const configuration = window.EventGalleryLightboxConfiguration;
        return configuration && configuration.KEY_CLOSE ? String(configuration.KEY_CLOSE).replace(/"/g, '&quot;') : 'Close';
    }


    _loadingText() {
        const configuration = window.EventGalleryLightboxConfiguration;
        return configuration && configuration.KEY_LOADING ? String(configuration.KEY_LOADING) : 'Loading …';
    }

    static _escape(text) {
        const span = document.createElement('span');
        span.textContent = text;
        return span.innerHTML;
    }

    _loadErrorText() {
        const configuration = window.EventGalleryLightboxConfiguration;
        return configuration && configuration.KEY_DIALOG_LOAD_FAILED ? String(configuration.KEY_DIALOG_LOAD_FAILED) : 'This could not be loaded.';
    }

    /**
     * Fetches the content of the dialog. An answer which is not 2xx - the page of an error, a
     * refusal - and a failed request end in onError instead of the callback: until 7.0.0 the
     * error page of the site landed in the dialog, or the spinner stayed for good.
     */
    static _getRemoteData(url, callback, fetchOptions = undefined, onError = undefined) {
        fetch(url, fetchOptions)
            .then(response => {
                if (!response.ok) {
                    throw new Error('HTTP ' + response.status);
                }
                return response.text();
            })
            .then(data => callback(data))
            .catch(error => {
                if (onError !== undefined) {
                    onError(error);
                } else {
                    console.error(error);
                }
            });
    }

    /**
     * moves the given content into the content overlay. Executes the onload method when finished.
     *
     * The content is markup only: a script in it does not run. Its behaviour comes from the
     * bundles (the format dialogs, the share dialog), so a dialog works under a Content Security
     * Policy without 'unsafe-inline' and 'unsafe-eval'. Until 7.0.0 the inline scripts of the
     * content ran through eval().
     *
     * @param content
     * @private
     */
    _addContent(content) {
        // the visitor closed the dialog while its content was on the way
        if (!this.myDialogElement) {
            return;
        }

        this.myDialogElement.innerHTML = content;
        this._prepareContent();

        if (this.onLoad !== undefined) {
            this.onLoad();
        }

        document.dispatchEvent(Polyfill.createNewEvent('eventgallery-images-added', { isOverlay: true }));
    }

    /**
     * Names the dialog, binds its close buttons and moves the focus into it: to the control
     * which carries autofocus (the copy button of the share dialog), so the keyboard starts
     * there and not on the dialog element; the content arrives after showModal(), which is
     * why the browser cannot do it itself.
     *
     * @private
     */
    _prepareContent() {
        this.myDialogElement.removeAttribute('aria-busy');

        // the dialog is named by its title (an .eg-title with an id in the content)
        const title = this.myDialogElement.querySelector('.eg-title[id]');
        if (title) {
            this.myDialogElement.setAttribute('aria-labelledby', title.id);
        }

        let elements = this.myDialogElement.getElementsByClassName('eventgallery-close-overlay');
        for (let i = 0; i < elements.length; i++) {
            elements[i].addEventListener('click', (e) => {
                e.preventDefault();
                this.closeOverlay()
            });
        }

        const autofocus = this.myDialogElement.querySelector('[autofocus]');
        if (autofocus && typeof autofocus.focus === 'function') {
            autofocus.focus();
        }
    }

    /**
     * The content could not be loaded: a sentence in place of the spinner and the close
     * button, which takes the focus.
     *
     * @private
     */
    _showLoadError() {
        if (!this.myDialogElement) {
            return;
        }

        const header = document.createElement('div');
        header.className = 'eg-dialog__header';
        header.innerHTML = '<div class="eg-dialog__heading"></div>'
            + '<button type="button" class="eg-btn eg-btn--quiet eg-btn--icon eg-dialog__close eventgallery-close-overlay" aria-label="' + this._closeLabel() + '" autofocus>'
            + icon('close') + '</button>';

        const notice = document.createElement('div');
        notice.className = 'eg-notice eg-notice--danger';
        notice.setAttribute('role', 'alert');
        notice.innerHTML = '<span class="eg-notice__icon">' + icon('alert') + '</span>';
        const text = document.createElement('div');
        text.className = 'eg-notice__body';
        text.textContent = this._loadErrorText();
        notice.appendChild(text);

        const body = document.createElement('div');
        body.className = 'eg-dialog__body';
        body.appendChild(notice);

        this.myDialogElement.replaceChildren(header, body);
        this._prepareContent();
    }

    /**
     * Closes the overlay and fires the onclose method if defined. Every way out comes here -
     * the close button, the backdrop, Escape through the close event of the dialog, the
     * lightbox when it changes the image or closes - so a second call does nothing.
     */
    closeOverlay() {
        const dialog = this.myDialogElement;
        this.isOpen = false;

        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }

        if (!dialog) {
            return;
        }
        this.myDialogElement = null;
        if (dialog.open) {
            dialog.close();
        }
        Polyfill.removeHtmlElementNode(dialog);

        if (this.onClosed !== undefined) {
            this.onClosed();
        }
    }
}
